import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Users, Bot, Mail, Sun, Moon, Sparkles, Zap, Globe, Target } from 'lucide-react';

function V4Minimalist() {
  const [isDark, setIsDark] = useState(true); // Default to dark mode

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    // If explicitly set to light, use light. Otherwise default to dark.
    if (savedTheme === 'light') {
      setIsDark(false);
      document.documentElement.classList.remove('dark');
    } else {
      setIsDark(true);
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark'); // Ensure it's saved
    }
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (!isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-neutral-900 dark:text-white transition-colors duration-500">
      {/* Decorative elements */}
      <div className="fixed top-0 right-0 w-96 h-96 bg-neutral-100 dark:bg-neutral-900 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
      <div className="fixed bottom-0 left-0 w-96 h-96 bg-neutral-100 dark:bg-neutral-900 rounded-full blur-3xl opacity-50 translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 dark:bg-black/80 backdrop-blur-xl border-b border-neutral-200 dark:border-neutral-800 z-50 transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="relative w-8 h-8 bg-black dark:bg-white transition-colors duration-500">
              <div className="absolute inset-2 border-2 border-white dark:border-black transition-colors duration-500"></div>
              <div className="absolute top-0 right-0 w-2 h-2 bg-white dark:bg-black transition-colors duration-500"></div>
            </div>
            <span className="text-xl font-semibold tracking-tight">Suri Associates</span>
          </div>
          <div className="flex items-center space-x-6">
            <a href="#approach" className="hidden md:block text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors">Approach</a>
            <a href="#capabilities" className="hidden md:block text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors">Capabilities</a>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button className="bg-black dark:bg-white text-white dark:text-black px-6 py-2 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-all">
              Contact Us
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl relative">
            {/* Animated badge */}
            <div className="inline-flex items-center space-x-2 border border-neutral-300 dark:border-neutral-700 px-4 py-2 mb-8 hover:border-black dark:hover:border-white transition-all duration-300 group">
              <div className="w-1.5 h-1.5 bg-black dark:bg-white animate-pulse"></div>
              <span className="text-xs font-medium tracking-wide uppercase text-neutral-600 dark:text-neutral-400 group-hover:text-black dark:group-hover:text-white transition-colors">Strategic talent arbitrage, not outsourcing</span>
            </div>
            
            {/* Headline with creative spacing */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light mb-8 leading-[1.05] tracking-tight">
              Elite Indian engineers.
              <br />
              <span className="inline-block mt-2">50 AI agents.</span>
              <br />
              <span className="font-semibold inline-block mt-2 relative">
                One team that outships everyone.
                <span className="absolute -bottom-2 left-0 w-full h-px bg-black dark:bg-white"></span>
              </span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-neutral-600 dark:text-neutral-400 mb-12 leading-relaxed max-w-3xl font-light">
              Suri Associates delivers enterprise-grade output without the traditional consulting bloat — combining world-class engineering talent with proprietary AI systems built for speed.
            </p>

            {/* CTA with hover effects */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <a href="https://calendly.com/harshit-suri-wz3c/30min" target="_blank" rel="noopener noreferrer" className="group relative bg-black dark:bg-white text-white dark:text-black px-8 py-4 text-base font-medium overflow-hidden transition-all hover:scale-105 inline-block text-center">
                <span className="relative z-10 flex items-center justify-center space-x-2">
                  <span>Schedule a free consultation</span>
                </span>
                <div className="absolute inset-0 bg-neutral-800 dark:bg-neutral-200 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
              </a>
              <a href="#approach" className="inline-flex items-center justify-center border-2 border-neutral-900 dark:border-white text-black dark:text-white px-8 py-4 text-base font-medium hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all">
                See how it works <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-16">30 minutes. No pitch. Immediate clarity on what's possible.</p>

            {/* Stats with creative layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-neutral-200 dark:border-neutral-800">
              <div className="group cursor-default">
                <div className="text-4xl font-semibold mb-2 group-hover:scale-110 transition-transform inline-block">35%</div>
                <div className="text-sm text-neutral-600 dark:text-neutral-400">faster delivery than traditional consulting teams</div>
              </div>
              <div className="group cursor-default">
                <div className="text-4xl font-semibold mb-2 group-hover:scale-110 transition-transform inline-block">60%</div>
                <div className="text-sm text-neutral-600 dark:text-neutral-400">lower cost than US or European agencies</div>
              </div>
              <div className="group cursor-default">
                <div className="text-4xl font-semibold mb-2 group-hover:scale-110 transition-transform inline-block">3–5×</div>
                <div className="text-sm text-neutral-600 dark:text-neutral-400">output per engineer vs a traditional developer</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 border-y border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 transition-colors duration-500">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-neutral-500 dark:text-neutral-600 text-xs font-medium uppercase tracking-wider mb-10">Our team brings experience from</p>
          <div className="flex flex-wrap justify-center items-center gap-12 lg:gap-24">
            <div className="text-center group">
              <div className="text-xl text-neutral-800 dark:text-neutral-200 font-semibold tracking-tight transition-colors group-hover:text-black dark:group-hover:text-white cursor-default">Bank of America</div>
              <div className="text-sm text-neutral-500 dark:text-neutral-500 mt-1">Enterprise engineering</div>
            </div>
            <div className="text-center group">
              <div className="text-xl text-neutral-800 dark:text-neutral-200 font-semibold tracking-tight transition-colors group-hover:text-black dark:group-hover:text-white cursor-default">Delta Airlines</div>
              <div className="text-sm text-neutral-500 dark:text-neutral-500 mt-1">Predictive technology</div>
            </div>
            <div className="text-center group">
              <div className="text-xl text-neutral-800 dark:text-neutral-200 font-semibold tracking-tight transition-colors group-hover:text-black dark:group-hover:text-white cursor-default">Gupshup</div>
              <div className="text-sm text-neutral-500 dark:text-neutral-500 mt-1">AI engineering</div>
            </div>
            <div className="text-center group">
              <div className="text-xl text-neutral-800 dark:text-neutral-200 font-semibold tracking-tight transition-colors group-hover:text-black dark:group-hover:text-white cursor-default">USF</div>
              <div className="text-sm text-neutral-500 dark:text-neutral-500 mt-1">Computer Science</div>
            </div>
          </div>
        </div>
      </section>

      {/* Track Record */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-8 h-px bg-neutral-300 dark:bg-neutral-700"></div>
              <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">TRACK RECORD</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
              Real results from real companies
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Enterprise-grade impact, delivered at startup speed.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-black p-10 border border-neutral-200 dark:border-neutral-800 hover:border-black dark:hover:border-white transition-colors group flex flex-col justify-between">
              <div>
                <div className="text-xs font-medium text-neutral-500 dark:text-neutral-500 mb-2">Results delivered as engineer at</div>
                <h3 className="text-xl font-semibold mb-6">Bank of America</h3>
                <div className="text-5xl font-light text-blue-600 dark:text-[#5e43f3] mb-6 group-hover:scale-105 transition-transform origin-left">30%</div>
                <p className="text-neutral-600 dark:text-neutral-400">UI latency reduction across 1,000+ system deployments</p>
              </div>
            </div>
            <div className="bg-white dark:bg-black p-10 border border-neutral-200 dark:border-neutral-800 hover:border-black dark:hover:border-white transition-colors group flex flex-col justify-between">
              <div>
                <div className="text-xs font-medium text-neutral-500 dark:text-neutral-500 mb-2">Results delivered as engineer at</div>
                <h3 className="text-xl font-semibold mb-6">Delta Airlines</h3>
                <div className="text-5xl font-light text-blue-600 dark:text-[#5e43f3] mb-6 group-hover:scale-105 transition-transform origin-left">60%</div>
                <p className="text-neutral-600 dark:text-neutral-400">reduction in manual data entry across 5 departments</p>
              </div>
            </div>
            <div className="bg-white dark:bg-black p-10 border border-neutral-200 dark:border-neutral-800 hover:border-black dark:hover:border-white transition-colors group flex flex-col justify-between">
              <div>
                <div className="text-xs font-medium text-neutral-500 dark:text-neutral-500 mb-2">Results delivered as engineer at</div>
                <h3 className="text-xl font-semibold mb-6">RegDev Inc.</h3>
                <div className="text-5xl font-light text-blue-600 dark:text-[#5e43f3] mb-6 group-hover:scale-105 transition-transform origin-left">80%</div>
                <p className="text-neutral-600 dark:text-neutral-400">reduction in manual workflow time via automation platform</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section id="capabilities" className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-20">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-8 h-px bg-neutral-300 dark:bg-neutral-700"></div>
              <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">OUR ADVANTAGE</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
              A structural edge
              <br />
              <span className="font-semibold">no competitor can replicate</span>
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Most firms either have the talent or the technology. We built a system that combines both — and the economics are permanent, not temporary.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-neutral-200 dark:bg-neutral-800">
            {/* Card 1 */}
            <div className="group bg-white dark:bg-black p-12 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-black dark:bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
              <div className="w-12 h-12 border-2 border-black dark:border-white mb-8 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500">
                <Globe className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-4 tracking-tight">Elite engineering talent</h3>
              <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-8">
                Senior engineers from India's top tech hubs. Silicon Valley standards. One-third the cost. This is not outsourcing — it's arbitrage.
              </p>
              <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-600">Cost savings</span>
                  <span className="text-2xl font-semibold">60–70%</span>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="group bg-white dark:bg-black p-12 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-black dark:bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
              <div className="w-12 h-12 border-2 border-black dark:border-white mb-8 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500">
                <Bot className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-4 tracking-tight">50+ proprietary AI agents</h3>
              <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-8">
                Custom agents handle code review, testing, documentation, and deployment around the clock. Engineers focus on architecture and judgment — never grunt work.
              </p>
              <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-600">Development cycle</span>
                  <span className="text-2xl font-semibold">24/7</span>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="group bg-white dark:bg-black p-12 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-black dark:bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
              <div className="w-12 h-12 border-2 border-black dark:border-white mb-8 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-4 tracking-tight">Multiplicative output</h3>
              <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-8">
                One engineer plus our AI agent stack equals the output of 3–5 traditional developers. The math compounds with every project.
              </p>
              <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-600">Output per engineer</span>
                  <span className="text-2xl font-semibold">3–5×</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="approach" className="py-24 px-6 lg:px-8 bg-neutral-50 dark:bg-neutral-950 transition-colors duration-500">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-20">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-8 h-px bg-neutral-300 dark:bg-neutral-700"></div>
              <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">HOW WE WORK</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
              Rigorous process.
              <br />
              <span className="font-semibold">Predictable outcomes.</span>
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Three steps. No proposals, no retainers for discovery, no waiting. You go from brief to production faster than most firms finish scoping.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Process Steps */}
            <div className="space-y-12">
              {[
                { 
                  num: '01', 
                  title: 'Discovery — Day 1', 
                  desc: 'You tell us your problem, we solve it. A 30-minute call to analyze your requirements, constraints, and success metrics. You leave with clarity on what we\'ll build, how long it takes, and exactly what it costs. No vague proposals.',
                  icon: Target,
                  metric: 'Fixed price within 24 hours'
                },
                { 
                  num: '02', 
                  title: 'Build — Days 2 to 21', 
                  desc: 'Senior architects design the solution. AI agents execute implementation, testing, and deployment continuously. Daily progress updates. Weekly live demos. You always know exactly where things stand.',
                  icon: Users,
                  metric: 'Human engineers + AI agents in parallel'
                },
                { 
                  num: '03', 
                  title: 'Deliver — Week 3 to 4', 
                  desc: 'Production-ready. Fully documented. Handed off clean. You own everything — code, IP, infrastructure. Zero dependency on us to operate what we built.',
                  icon: Sparkles,
                  metric: 'No lock-in. Ever.'
                }
              ].map((step) => (
                <div key={step.num} className="group flex gap-8 hover:translate-x-2 transition-transform duration-300">
                  <div className="flex-shrink-0">
                    <div className="text-6xl font-light text-neutral-200 dark:text-neutral-800 group-hover:text-black dark:group-hover:text-white transition-colors">{step.num}</div>
                  </div>
                  <div className="pt-2">
                    <div className="flex items-center space-x-3 mb-3">
                      <step.icon className="w-5 h-5 text-neutral-400 dark:text-neutral-600" />
                      <h4 className="text-xl font-semibold tracking-tight">{step.title}</h4>
                    </div>
                    <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-3">{step.desc}</p>
                    <span className="inline-block text-sm font-medium text-black dark:text-white bg-neutral-200 dark:bg-neutral-800 px-3 py-1">{step.metric}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Metrics */}
            <div className="bg-white dark:bg-black border-2 border-neutral-200 dark:border-neutral-800 p-10 hover:border-black dark:hover:border-white transition-all duration-300">
              <div className="space-y-6">
                <div className="pb-6 border-b border-neutral-200 dark:border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Human Engineers</span>
                    <span className="text-2xl font-semibold">3-5</span>
                  </div>
                </div>
                <div className="pb-6 border-b border-neutral-200 dark:border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">AI Agents</span>
                    <span className="text-2xl font-semibold">50+</span>
                  </div>
                </div>
                <div className="pb-6 border-b border-neutral-200 dark:border-neutral-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Development Hours</span>
                    <span className="text-2xl font-semibold">24/7</span>
                  </div>
                </div>
                <div className="pt-2 bg-neutral-50 dark:bg-neutral-950 -mx-10 -mb-10 px-10 pb-10 mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Effective Output</span>
                    <span className="text-3xl font-bold">15-25</span>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-600 mt-2">Equivalent developer capacity</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-8 h-px bg-neutral-300 dark:bg-neutral-700"></div>
              <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">OUR WORK</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
              ModelSight
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
              We built an enterprise-grade LLM observability layer to prove our process. 
            </p>
          </div>

          <div className="bg-neutral-50 dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 overflow-hidden group">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <div className="inline-block px-3 py-1 bg-black text-white dark:bg-white dark:text-black text-xs font-semibold tracking-wider uppercase mb-8 w-max">
                  Internal Product
                </div>
                <h3 className="text-3xl font-semibold mb-6 tracking-tight">Real-time LLM observability</h3>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed mb-8">
                  ModelSight provides a reliability and observability layer for LLM applications. Built from scratch with a proprietary engine to track requests, catch hallucinations, monitor costs, and analyze latency in real-time.
                </p>
                <div className="grid grid-cols-2 gap-8 pt-8 border-t border-neutral-200 dark:border-neutral-800 mb-8">
                  <div>
                    <div className="text-sm text-neutral-500 dark:text-neutral-500 mb-1">Timeline</div>
                    <div className="font-semibold">3 weeks</div>
                  </div>
                  <div>
                    <div className="text-sm text-neutral-500 dark:text-neutral-500 mb-1">Stack</div>
                    <div className="font-semibold">React, Node, Python</div>
                  </div>
                </div>
                <div>
                  <a href="https://modelsight.net" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 font-medium hover:text-neutral-600 dark:hover:text-neutral-400 transition-colors">
                    <span>View live product</span>
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <div className="bg-neutral-100 dark:bg-neutral-900 relative min-h-[400px] lg:min-h-full flex items-center justify-center p-8 overflow-hidden">
                <div className="absolute inset-0 opacity-20 group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
                
                {/* Mock UI replacing grey boxes with actual dashboard look */}
                <div className="relative z-10 w-full max-w-2xl bg-white dark:bg-black shadow-2xl rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden transform group-hover:-translate-y-2 transition-transform duration-500 font-sans">
                  {/* Mock Browser Header */}
                  <div className="h-10 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 flex items-center px-4 space-x-2">
                    <div className="flex space-x-1.5 mr-4">
                      <div className="w-3 h-3 rounded-full bg-neutral-300 dark:bg-neutral-700"></div>
                      <div className="w-3 h-3 rounded-full bg-neutral-300 dark:bg-neutral-700"></div>
                      <div className="w-3 h-3 rounded-full bg-neutral-300 dark:bg-neutral-700"></div>
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium tracking-wide">ModelSight Dashboard</div>
                  </div>
                  
                  {/* Dashboard Content */}
                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      
                      {/* Metric 1 */}
                      <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Requests (24h)</div>
                        <div className="text-2xl font-semibold text-black dark:text-white mb-2 tracking-tight">24,847</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-500">↑ 12% from yesterday</div>
                      </div>

                      {/* Metric 2 */}
                      <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Hallucinations Caught</div>
                        <div className="text-2xl font-semibold text-black dark:text-white mb-2 tracking-tight">127</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-500">0.5% of requests</div>
                      </div>

                      {/* Metric 3 */}
                      <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Cost Today</div>
                        <div className="text-2xl font-semibold text-black dark:text-white mb-2 tracking-tight">$47.23</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-500">↓ 8% optimized</div>
                      </div>

                      {/* Metric 4 */}
                      <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-4">
                        <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">Avg Latency</div>
                        <div className="text-2xl font-semibold text-black dark:text-white mb-2 tracking-tight">234ms</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">P95: 890ms</div>
                      </div>

                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
          
          <div className="mt-12 p-8 bg-neutral-50 dark:bg-neutral-900 border-l-2 border-black dark:border-white">
            <p className="text-lg font-medium">
              This is exactly how we build for clients. Same team. Same process. Same speed.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-24 px-6 lg:px-8 bg-neutral-50 dark:bg-neutral-950 transition-colors duration-500">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-8 h-px bg-neutral-300 dark:bg-neutral-700"></div>
              <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">COMPETITIVE POSITIONING</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
              How we compare
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Every other option asks you to choose between quality, speed, and cost. We don't.
            </p>
          </div>
          
          <div className="bg-white dark:bg-black border border-neutral-200 dark:border-neutral-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left py-5 px-6 text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-600"></th>
                    <th className="text-center py-5 px-6">
                      <div className="font-semibold text-base uppercase tracking-wider text-black dark:text-white">Suri Associates</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-600 mt-1">(You)</div>
                    </th>
                    <th className="text-center py-5 px-6 text-sm font-medium uppercase tracking-wider text-neutral-600 dark:text-neutral-400">Big Consulting</th>
                    <th className="text-center py-5 px-6 text-sm font-medium uppercase tracking-wider text-neutral-600 dark:text-neutral-400">US Boutique</th>
                    <th className="text-center py-5 px-6 text-sm font-medium uppercase tracking-wider text-neutral-600 dark:text-neutral-400">Traditional Offshore</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-neutral-100 dark:border-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-neutral-600 dark:text-neutral-400">Cost</td>
                    <td className="text-center py-4 px-6 font-semibold text-black dark:text-white">$$</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">$$$$</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">$$$</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">$</td>
                  </tr>
                  <tr className="border-b border-neutral-100 dark:border-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-neutral-600 dark:text-neutral-400">Delivery speed</td>
                    <td className="text-center py-4 px-6 font-semibold text-black dark:text-white">Fastest</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">Slow</td>
                    <td className="text-center py-4 px-6 text-neutral-500 dark:text-neutral-500">Fast</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">Medium</td>
                  </tr>
                  <tr className="border-b border-neutral-100 dark:border-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-neutral-600 dark:text-neutral-400">AI-native</td>
                    <td className="text-center py-4 px-6 font-semibold text-black dark:text-white">Built in</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">Retrofitted</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">Limited</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">None</td>
                  </tr>
                  <tr className="border-b border-neutral-100 dark:border-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-neutral-600 dark:text-neutral-400">Quality standard</td>
                    <td className="text-center py-4 px-6 font-semibold text-black dark:text-white">Enterprise</td>
                    <td className="text-center py-4 px-6 text-neutral-500 dark:text-neutral-500">Enterprise</td>
                    <td className="text-center py-4 px-6 text-neutral-500 dark:text-neutral-500">Enterprise</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-600">Variable</td>
                  </tr>
                  <tr className="border-b border-neutral-100 dark:border-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-neutral-600 dark:text-neutral-400">24/7 operations</td>
                    <td className="text-center py-4 px-6 font-semibold text-black dark:text-white">Yes</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-700">No</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-700">No</td>
                    <td className="text-center py-4 px-6 text-neutral-500 dark:text-neutral-500">Yes</td>
                  </tr>
                  <tr className="hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
                    <td className="py-4 px-6 text-sm font-medium text-neutral-600 dark:text-neutral-400">Fixed pricing</td>
                    <td className="text-center py-4 px-6 font-semibold text-black dark:text-white">Always</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-700">Never</td>
                    <td className="text-center py-4 px-6 text-neutral-400 dark:text-neutral-700">Rarely</td>
                    <td className="text-center py-4 px-6 text-neutral-500 dark:text-neutral-500">Rarely</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-8 h-px bg-neutral-300 dark:bg-neutral-700"></div>
              <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">SERVICES</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
              Three ways to work with us
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Fixed scope. Fixed price. Every time.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="border border-neutral-200 dark:border-neutral-800 p-8 hover:border-black dark:hover:border-white transition-colors duration-300">
              <h3 className="text-xl font-semibold mb-4 tracking-tight">AI strategy sprint</h3>
              <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-8">
                Two weeks. We map your top AI opportunities and deliver a working prototype — not a slide deck.
              </p>
            </div>

            {/* Service 2 */}
            <div className="border border-neutral-200 dark:border-neutral-800 p-8 hover:border-black dark:hover:border-white transition-colors duration-300">
              <h3 className="text-xl font-semibold mb-4 tracking-tight">AI agents & automation</h3>
              <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-8">
                Custom agents replacing your most expensive manual workflows. Shipped in weeks, not quarters.
              </p>
            </div>

            {/* Service 3 */}
            <div className="border border-neutral-200 dark:border-neutral-800 p-8 hover:border-black dark:hover:border-white transition-colors duration-300">
              <h3 className="text-xl font-semibold mb-4 tracking-tight">Fractional AI partner</h3>
              <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-8">
                Ongoing strategy and implementation. We act as your in-house AI team without the headcount.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-24 px-6 lg:px-8 bg-neutral-50 dark:bg-neutral-950 transition-colors duration-500">
        <div className="max-w-4xl mx-auto">
          <div className="mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <div className="w-8 h-px bg-neutral-300 dark:bg-neutral-700"></div>
              <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">AUTHORITY, NOT RESUME</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
              Driven by experience
            </h2>
          </div>

          <div className="bg-white dark:bg-black border border-neutral-200 dark:border-neutral-800 rounded-2xl md:rounded-3xl overflow-hidden shadow-sm">
            <div className="p-10 md:p-12 flex flex-col md:flex-row gap-8">
              <div className="w-24 h-24 rounded-full bg-[#1e1e3a] text-[#8e8efa] flex items-center justify-center text-3xl font-semibold tracking-tighter shrink-0 border border-[#2a2a4a]">
                HS
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-1">Harshit Suri</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 font-medium">
                  Founder & Managing Partner · Computer Science, University of South Florida
                </p>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  Before Suri Associates, I built automation and AI systems at Bank of America, Delta Airlines, and Gupshup — shipping tools that reduced manual workloads by 60–80% at enterprise scale. I started this firm because I kept seeing the same gap: companies that needed serious AI capability but couldn't afford big consulting rates or wait 6 months for delivery. We solve that. Every client gets my direct involvement from day one.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 bg-neutral-50 dark:bg-[#151515] border-t border-neutral-200 dark:border-neutral-800 divide-y md:divide-y-0 md:divide-x divide-neutral-200 dark:divide-neutral-800">
              <div className="p-8 group cursor-default">
                <div className="text-4xl font-light text-blue-600 dark:text-[#5e43f3] mb-2 group-hover:scale-105 transition-transform origin-left inline-block">4</div>
                <div className="text-sm text-neutral-600 dark:text-neutral-400">companies shipped at</div>
              </div>
              <div className="p-8 group cursor-default">
                <div className="text-4xl font-light text-blue-600 dark:text-[#5e43f3] mb-2 group-hover:scale-105 transition-transform origin-left inline-block">12+</div>
                <div className="text-sm text-neutral-600 dark:text-neutral-400">AI & automation systems built</div>
              </div>
              <div className="p-8 group cursor-default">
                <div className="text-4xl font-light text-blue-600 dark:text-[#5e43f3] mb-2 group-hover:scale-105 transition-transform origin-left inline-block">6 wks</div>
                <div className="text-sm text-neutral-600 dark:text-neutral-400">avg time to production</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-black dark:bg-white text-white dark:text-black p-16 lg:p-20 overflow-hidden group">
            <div className="absolute inset-0 bg-white dark:bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
            <div className="relative max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-light mb-6 tracking-tight">
                Ready to outship
                <br />
                <span className="font-semibold">your competition?</span>
              </h2>
              <p className="text-lg text-neutral-300 dark:text-neutral-700 mb-10 leading-relaxed">
                Schedule 30 minutes with Harshit. No pitch. Just an honest look at what's possible for your business.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href="https://calendly.com/harshit-suri-wz3c/30min" target="_blank" rel="noopener noreferrer" className="inline-block text-center bg-white dark:bg-black text-black dark:text-white px-8 py-4 font-medium hover:bg-neutral-200 dark:hover:bg-neutral-900 transition-colors whitespace-nowrap border-2 border-white dark:border-black">
                  Schedule a free consultation
                </a>
                <a href="mailto:harshit@suri.associates" className="flex items-center justify-center px-8 py-4 border-2 border-neutral-800 dark:border-neutral-200 hover:bg-neutral-900 dark:hover:bg-neutral-100 transition-colors">
                  Email harshit@suri.associates <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>

              <div className="flex flex-wrap gap-8 text-sm text-neutral-400 dark:text-neutral-600">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4" />
                  <span>No commitment</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4" />
                  <span>30 minutes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4" />
                  <span>Immediate pricing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4" />
                  <span>No sales pitch</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-200 dark:border-neutral-800 py-12 px-6 lg:px-8 transition-colors duration-500">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="relative w-8 h-8 bg-black dark:bg-white transition-colors duration-500">
                  <div className="absolute inset-2 border-2 border-white dark:border-black transition-colors duration-500"></div>
                  <div className="absolute top-0 right-0 w-2 h-2 bg-white dark:bg-black transition-colors duration-500"></div>
                </div>
                <span className="text-lg font-semibold tracking-tight">Suri Associates</span>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Enterprise-grade AI consulting at unprecedented speed and cost efficiency.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Company</h4>
              <ul className="space-y-3 text-sm text-neutral-600 dark:text-neutral-400">
                <li><a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">About</a></li>
                <li><a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">Case Studies</a></li>
                <li><a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">Careers</a></li>
                <li><a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Services</h4>
              <ul className="space-y-3 text-sm text-neutral-600 dark:text-neutral-400">
                <li><a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">AI Development</a></li>
                <li><a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">Web Applications</a></li>
                <li><a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">Mobile Solutions</a></li>
                <li><a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">Cloud Infrastructure</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">Contact</h4>
              <a href="mailto:hello@suri.associates" className="inline-flex items-center space-x-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                <span>hello@suri.associates</span>
              </a>
            </div>
          </div>
          
          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-neutral-500 dark:text-neutral-600">
            <p>© 2025 Suri Associates. All rights reserved.</p>
            <div className="flex space-x-8 mt-4 md:mt-0">
              <a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">Privacy</a>
              <a href="/" className="hover:text-black dark:hover:text-white transition-colors cursor-pointer">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default V4Minimalist;
